/**
 * Docue Agent attachment selection, validation, and Eve file-part helpers.
 *
 * The media-type list and byte cap mirror
 * agents/platform-agent/agent/lib/docue-agent-attachments.ts — keep both in sync.
 */

import type { SendTurnInput } from 'eve/client'

interface DocueAgentAttachmentType {
  /** Extra media types browsers report for this format. */
  aliases?: readonly string[]
  extensions: readonly string[]
  icon: string
  label: string
}

/** Single source of truth: the picker filter, icons, and labels all derive from this. */
const DOCUE_AGENT_ATTACHMENT_TYPES = {
  'application/pdf': {
    extensions: ['pdf'],
    icon: 'docue:file-pdf-01',
    label: 'PDF',
  },
  'text/plain': {
    extensions: ['txt', 'text'],
    icon: 'docue:file-01',
    label: 'TXT',
  },
  'text/csv': {
    aliases: ['application/csv'],
    extensions: ['csv'],
    icon: 'docue:file-01',
    label: 'CSV',
  },
  'image/jpeg': {
    aliases: ['image/jpg', 'image/pjpeg'],
    extensions: ['jpg', 'jpeg'],
    icon: 'docue:image-01',
    label: 'JPEG',
  },
  'image/png': {
    extensions: ['png'],
    icon: 'docue:image-01',
    label: 'PNG',
  },
  'image/webp': {
    extensions: ['webp'],
    icon: 'docue:image-01',
    label: 'WebP',
  },
  'application/msword': {
    extensions: ['doc'],
    icon: 'docue:file-ms-word-01',
    label: 'DOC',
  },
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document': {
    extensions: ['docx'],
    icon: 'docue:file-ms-word-01',
    label: 'DOCX',
  },
  'application/vnd.ms-excel': {
    extensions: ['xls'],
    icon: 'docue:table',
    label: 'XLS',
  },
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': {
    extensions: ['xlsx'],
    icon: 'docue:table',
    label: 'XLSX',
  },
} as const satisfies Record<string, DocueAgentAttachmentType>

export type DocueAgentSupportedMediaType = keyof typeof DOCUE_AGENT_ATTACHMENT_TYPES

const ATTACHMENT_TYPE_ENTRIES = Object.entries(DOCUE_AGENT_ATTACHMENT_TYPES) as [
  DocueAgentSupportedMediaType,
  DocueAgentAttachmentType,
][]

export const DOCUE_AGENT_MAX_ATTACHMENT_BYTES = 3 * 1024 * 1024
export const DOCUE_AGENT_MAX_ATTACHMENT_COUNT = 5

export const DOCUE_AGENT_SUPPORTED_MEDIA_TYPES = ATTACHMENT_TYPE_ENTRIES
  .map(([mediaType]) => mediaType)

/** MIME types plus extensions for the file picker (browsers often omit Office MIME types). */
export const DOCUE_AGENT_ATTACHMENT_ACCEPT = ATTACHMENT_TYPE_ENTRIES
  .flatMap(([mediaType, type]) => [
    mediaType,
    ...type.extensions.map(extension => `.${extension}`),
  ])
  .join(',')

const MEDIA_TYPES_BY_ALIAS = new Map<string, DocueAgentSupportedMediaType>(
  ATTACHMENT_TYPE_ENTRIES.flatMap(([mediaType, type]) =>
    [mediaType, ...type.aliases ?? []].map(alias => [alias, mediaType] as const),
  ),
)

const MEDIA_TYPES_BY_EXTENSION = new Map<string, DocueAgentSupportedMediaType>(
  ATTACHMENT_TYPE_ENTRIES.flatMap(([mediaType, type]) =>
    type.extensions.map(extension => [extension, mediaType] as const),
  ),
)

export interface DocueAgentPendingAttachment {
  /** Plain base64 payload, resolved at attach time; in memory only, never persisted. */
  data: string
  id: string
  mediaType: DocueAgentSupportedMediaType
  name: string
  size: number
}

export type DocueAgentAttachmentValidationError
  = | { code: 'unsupported-type', name: string, mediaType: string }
    | { code: 'too-many', limit: number }
    | { code: 'too-large', name: string, size: number, limit: number }
    | { code: 'aggregate-too-large', size: number, limit: number }
    | { code: 'invalid-encoding', name: string }

export function resolveDocueAgentMediaType(file: {
  name: string
  type: string
}): DocueAgentSupportedMediaType | undefined {
  const declared = MEDIA_TYPES_BY_ALIAS.get(file.type.trim().toLowerCase())
  if (declared)
    return declared

  const extension = file.name.split('.').pop()?.trim().toLowerCase()
  if (!extension)
    return undefined

  return MEDIA_TYPES_BY_EXTENSION.get(extension)
}

export function docueAgentAttachmentIcon(mediaType: string): string {
  return DOCUE_AGENT_ATTACHMENT_TYPES[mediaType as DocueAgentSupportedMediaType]?.icon
    ?? 'docue:file-01'
}

export function docueAgentAttachmentTypeLabel(mediaType: string): string {
  return DOCUE_AGENT_ATTACHMENT_TYPES[mediaType as DocueAgentSupportedMediaType]?.label
    ?? mediaType
}

function isUtf8Bytes(bytes: Uint8Array): boolean {
  try {
    new TextDecoder('utf-8', { fatal: true }).decode(bytes)
    return true
  }
  catch {
    return false
  }
}

export async function validateDocueAgentAttachmentFile(
  file: File,
  existing: readonly DocueAgentPendingAttachment[],
): Promise<DocueAgentAttachmentValidationError | null> {
  if (existing.length >= DOCUE_AGENT_MAX_ATTACHMENT_COUNT) {
    return {
      code: 'too-many',
      limit: DOCUE_AGENT_MAX_ATTACHMENT_COUNT,
    }
  }

  const mediaType = resolveDocueAgentMediaType(file)
  if (!mediaType) {
    return {
      code: 'unsupported-type',
      name: file.name,
      mediaType: file.type || 'unknown',
    }
  }

  if (file.size <= 0 || file.size > DOCUE_AGENT_MAX_ATTACHMENT_BYTES) {
    return {
      code: 'too-large',
      name: file.name,
      size: file.size,
      limit: DOCUE_AGENT_MAX_ATTACHMENT_BYTES,
    }
  }

  const nextTotal = existing.reduce((sum, item) => sum + item.size, 0) + file.size
  if (nextTotal > DOCUE_AGENT_MAX_ATTACHMENT_BYTES) {
    return {
      code: 'aggregate-too-large',
      size: nextTotal,
      limit: DOCUE_AGENT_MAX_ATTACHMENT_BYTES,
    }
  }

  if (mediaType.startsWith('text/')) {
    const bytes = new Uint8Array(await file.arrayBuffer())
    if (!isUtf8Bytes(bytes)) {
      return {
        code: 'invalid-encoding',
        name: file.name,
      }
    }
  }

  return null
}

/** Strips the `data:<mediaType>;base64,` prefix — Eve file parts take plain base64. */
async function fileToPlainBase64(file: File): Promise<string> {
  const dataUrl = await readAsDataURLAsync(file)
  return dataUrl.slice(dataUrl.indexOf(',') + 1)
}

export async function createDocueAgentPendingAttachment(
  file: File,
): Promise<DocueAgentPendingAttachment> {
  const mediaType = resolveDocueAgentMediaType(file)
  if (!mediaType)
    throw new Error(`Unsupported Docue Agent attachment type: ${file.type || file.name}`)

  return {
    data: await fileToPlainBase64(file),
    id: crypto.randomUUID(),
    mediaType,
    name: file.name,
    size: file.size,
  }
}

/**
 * Builds Eve `UserContent` using plain base64 file parts (not data URLs), so
 * confirmed `delivery.consumed` events stay metadata-only.
 */
export function buildDocueAgentUserContent(
  text: string,
  attachments: readonly DocueAgentPendingAttachment[],
): SendTurnInput['message'] {
  const trimmed = text.trim()
  if (attachments.length === 0)
    return trimmed

  return [
    ...trimmed
      ? [{
          type: 'text' as const,
          text: trimmed,
        }]
      : [],
    ...attachments.map(attachment => ({
      type: 'file' as const,
      data: attachment.data,
      mediaType: attachment.mediaType,
      filename: attachment.name,
    })),
  ]
}
